<template>
    <q-card id="speciesSummaryPane">
        <q-card-section class="q-pt-none q-pl-sm" v-bind="attrs" v-if="finalData">
            <div class="text-body2 q-pa-sm" >
                <div class="text-h7 underline text-uppercase">Species Diversity</div>
                <ul  class="q-mt-xs">
                    <li  v-if="finalData['overallDiv'] !=''">The area is considered overall high diversity for <span class="text-bold">{{ finalData['overallDiv'] }}</span></li>
                    <li >The season when demersal fish diversity is highest is &nbsp;<span class="text-bold">{{ finalData['fishDivSeason'] }}</span></li>
                    
                    <li v-if="finalData['mammalDivCount'] && finalData['mammalDivCount'] > 1">The month(s) when marine mammal diversity is highest ({{ finalData['mammalDivCount'] }} species at very high levels): <span class="text-bold">{{ finalData['mammalDivMonth'] }}</span></li>
                    <li v-else>There are no months when &gt;1 marine mammal species is found at very high levels.</li>
                    
                    <li v-if="finalData['birdDivCount'] && finalData['birdDivCount'] > 1">The month(s) when offshore bird diversity is highest ({{ finalData['birdDivCount'] }} species at very high levels): <span class="text-bold">{{ finalData['birdDivMonth'] }}</span></li>
                    <li v-else>There are no months when &gt;1 bird species is found at very high levels.</li>
               
                    <li v-if="finalData['coastalBirdDivCount'] && finalData['coastalBirdDivCount'] > 1">The month(s) when coastal bird diversity is highest ({{ finalData['coastalBirdDivCount'] }} species at very high levels): <span class="text-bold">{{ finalData['coastalBirdDivMonth'] }}</span></li>
                    <li v-else>There are no months when &gt;1 bird species is found at very high levels.</li>
                </ul>
            </div>  

            <div class="text-body2 q-pa-sm" >
                <div class="text-h7 underline text-uppercase">Key Species & Habitats</div>
                <ul  class="q-mt-xs">
                    <li>
                        ESA-listed marine mammals found in the area at high levels: 
                        <span class="text-bold">{{ finalData['summaryItems']['esaMammals'] }} </span>
                    </li>
                    <li>
                        ESA-listed birds found in the area at high levels: 
                        <span class="text-bold">{{ finalData['summaryItems']['esaBirds'] }}</span>
                    </li>
                    <li>
                        Bird species considered endangered by at least one state: 
                        <span class="text-bold">{{ finalData['summaryItems']['stateBirds'] }}</span>
                    </li>
                    <li >
                        Fish species considered of high conservation need by NE RSGCN that are found persistently in the area: 
                        <span  class="text-bold">{{ finalData['summaryItems']['sgcnFish'] }}</span>
                    </li>
                </ul>

                <div class="text-body2 text-italic">Coastal birds:</div>
                <ul class="q-mt-xs">
                    <li>
                        Species of birds that are ESA listed: 
                        <span class="text-bold"  v-if="finalData['summaryItems']['esaCoastalBirds'] !=''">{{ finalData['summaryItems']['esaCoastalBirds'] }}</span>
                        <span v-else class="text-bold">None</span>
                    </li>
                    <li>
                        Species of birds in the area that are listed as endangered or threatened by one of the states: 
                        <span class="text-bold"  v-if="finalData['summaryItems']['stateCoastalBirds'] !=''">{{ finalData['summaryItems']['stateCoastalBirds'] }}</span>
                        <span v-else class="text-bold">None</span>
                    </li>
                    <li>
                        Species of birds that are considered of high conservation need: 
                        <span class="text-bold"  v-if="finalData['summaryItems']['sgcnCoastalBirds'] !=''">{{ finalData['summaryItems']['sgcnCoastalBirds'] }}</span>
                        <span v-else class="text-bold">None</span>
                    </li>
                </ul>
            </div> 

            <div class="text-body2 q-pa-sm" v-if="finalData['summaryItems']['saltmarsh'] !='None / No Data' ||
                    finalData['summaryItems']['sand'] !='None / No Data' ||
                    finalData['summaryItems']['rocky'] !='None / No Data'">
                <div class="text-h7 underline text-uppercase">Connections</div>
                <ul  class="q-mt-xs">
                    <li v-if="finalData['overallDiv'].includes('demersal fish')">
                        Demersal fish species depend on Features such as hardbottom and high complexity areas. Visit the 
                        <span class="myLink" @click="goTo({'link' :'../features'})"> FEATURES</span> tab for more information. 
                    </li>
                    <li>
                        While several bird species use this area offshore, some of them also use the following coastal habitats:
                        <ul>
                            <li v-if="finalData['summaryItems']['saltmarsh'] !=''"> 
                                <span class="text-bold">{{ finalData['summaryItems']['saltmarsh'] }}</span> depend on 
                                <span class="myLink" @click="goTo({'link' :'../../habSpotlight', 'params' : {'type' : 'habSpotlight', 'value' : 'wetlands'}})">
                                    COASTAL SALTMARSHES
                                </span> during part of the year. 
                            </li>
                            <li v-if="finalData['summaryItems']['sand'] !=''"> 
                                <span class="text-bold">{{ finalData['summaryItems']['sand'] }}</span> depend on 
                                <span class="myLink" @click="goTo({'link' :'../../habSpotlight', 'params' : {'type' : 'habSpotlight', 'value' : 'sand'}})">
                                    COASTAL SAND DUNES
                                </span> during part of the year. 
                            </li>
                            <li v-if="finalData['summaryItems']['rocky'] !=''"> 
                                <span class="text-bold">{{ finalData['summaryItems']['rocky'] }}</span> depend on 
                                <span class="myLink" @click="goTo({'link' :'../../habSpotlight', 'params' : {'type' : 'habSpotlight', 'value' : 'rocky'}})">
                                    ROCKY SHORES
                                </span> during their breeding seasons. 
                            </li>
                            <!-- <li v-if="finalData['KCOR'] == 2">
                                <span class="text-bold">Confirmed coral</span> habitat is often indiciative of 
                                <span class="myLink" @click="goTo({'link' :'../features/seafloor'})">
                                    hardbottom present.
                                </span>
                            </li> -->
                        </ul>
                    </li>
                </ul>
            </div>
            <div class="text-caption text-italic">
                Be aware that this list doesn't represent all species that use these habitats. Go to <span class="pointer myLink text-body2" @click="goTo({'link' :'../coastal/summary'})">COASTAL</span> tab for more information. 
                This list does not reflect any mitigation or conservation requirements already in place in this area.
            </div>

        </q-card-section>

    </q-card>
</template>
<script setup>
import { toRefs, onMounted, useAttrs, computed} from "vue";
import { useHelpers } from "../composables/useHelpers"
import { useMapItems } from "../composables/useMapItems"
import { stops } from "../assets/ColorStops.json"
import { AllCodes } from "../assets/SppCodesToNames.json"
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()
const {  tileLayers, toggleLayer, print, printing, styleMap, setLegend, loadDataByID, activeLayerID, processedData, setProcessedData, setActiveHabSpotlight } = useMapItems()
const { arrayFindObjectByProp, arrayListToString, getPropForLargestValue, getPropsForLargestValue } = useHelpers();
const attrs = useAttrs();

const props = defineProps({
    data: { type: Object },
    version: {type: Number}
})

const { data, version } = toRefs(props)

const summaryData =  computed(() =>{
    if (data.value && data.value["SPECIES_SUMMARY"] && data.value["SPECIES_SUMMARY"].length >0){
        return data.value["SPECIES_SUMMARY"]
    }
    else{ return null}
})

const finalData = computed(() => {
    if (
        !processedData.value[version.value] || 
        processedData.value[version.value]['SPECIES_SUMMARY'] === undefined ||
        processedData.value[version.value]['SPECIES_SUMMARY']['a'] === undefined ||
        processedData.value[version.value]['SPECIES_SUMMARY']['a']['a'] === undefined
    ){
        if (summaryData.value) {
            const finalData = {
                "overallDiv": "",
            }
            const workingValues = {}

            ///////// CONFIGURE working objects
            // setup object for overall species diversity
            const diverseGroupsAttrs = ["DIV_FIS", "DIV_MM", "DIV_BIO", "DIV_BIC"]
            diverseGroupsAttrs.forEach((attr) =>{
                workingValues[attr] = false
            })

            // setup object for most diverse fish season
            const fishDivSeasonAttrs = ["DIV_FISS",  "DIV_FISF" ]
            fishDivSeasonAttrs.forEach((attr) =>{
                workingValues[attr] = []
            })

            // setup object for most diverse mammal month
            const mmDivMonthAttrs = ["DIV_MM01", "DIV_MM02", "DIV_MM03", "DIV_MM04", "DIV_MM05", "DIV_MM06", "DIV_MM07", "DIV_MM08","DIV_MM09","DIV_MM10","DIV_MM11", "DIV_MM12"]
            mmDivMonthAttrs.forEach((attr) =>{
                workingValues[attr] = []
            })
            
            // setup object for most diverse bird month
            const birdDivMonthAttrs = ["DIV_BIO01", "DIV_BIO02", "DIV_BIO03", "DIV_BIO04", "DIV_BIO05", "DIV_BIO06", "DIV_BIO07", "DIV_BIO08","DIV_BIO09","DIV_BIO10","DIV_BIO11", "DIV_BIO12"]
            birdDivMonthAttrs.forEach((attr) =>{
                workingValues[attr] = []
            })
            
            const coastalBirdDivMonthAttrs = ["DIV_BIC_01", "DIV_BIC_02", "DIV_BIC_03", "DIV_BIC_04", "DIV_BIC_05", "DIV_BIC_06", "DIV_BIC_07", "DIV_BIC_08","DIV_BIC_09","DIV_BIC_10","DIV_BIC_11", "DIV_BIC_12"]
            coastalBirdDivMonthAttrs.forEach((attr) =>{
                workingValues[attr] = []
            })

            // setup for metrics that report on something being estimated or confirmed
            const estConfMetrics = ["KCOR"]
            estConfMetrics.forEach((attr) =>{
                workingValues[attr] = []
            })

            // setup for endangered/threated/SGCN species
            for (const [theme, spArr] of Object.entries(potentialSPLists)) {
                workingValues[theme] ={}
                spArr.forEach((attr) =>{
                    // the summary table includes coastal birds and offshaore birds of the same sp, so CB_ is used to identify the coastal 
                    // species.  But once the data is processed, remove the CB_ flag
                    const cleanAttr = attr.replace("CB_", "") 
                    workingValues[theme][cleanAttr] = []
                })
            }
           

            /////////// PROCESS CELL DATA
            summaryData.value.forEach((cell) => {

                // overall species diversity
                diverseGroupsAttrs.forEach((attr) =>{
                    if (cell[attr] == 1){
                        workingValues[attr] = true
                    }
                })

                // most diverse fish season
                fishDivSeasonAttrs.forEach((attr) =>{
                    workingValues[attr].push(cell[attr])
                })

                // most diverse mammal month
                mmDivMonthAttrs.forEach((attr) =>{
                    workingValues[attr].push(cell[attr])
                })

                // most diverse bird month
                birdDivMonthAttrs.forEach((attr) =>{
                    workingValues[attr].push(cell[attr])
                })

                // most diverse coastal bird Month 
                coastalBirdDivMonthAttrs.forEach((attr) =>{
                    workingValues[attr].push(cell[attr])
                })

                // estimated (1) / confirmed (2))
                estConfMetrics.forEach((attr) =>{
                    workingValues[attr].push(cell[attr])
                })

                // populate cell values into endangered/threated/SGCN species object
                for (const [theme, spArr] of Object.entries(potentialSPLists)) {
                    spArr.forEach((attr) =>{
                        // the summary table includes coastal birds and offshaore birds of the same sp, so CB_ is used to identify the coastal 
                        // species.  But once the data is processed, remove the CB_ flag
                        const cleanAttr = attr.replace("CB_", "") 
                        workingValues[theme][cleanAttr].push(cell[attr])
                    })
                }
            })
            


            /////// PROCESS FINAL DATA
            // calc final overall species diversity
            const overallDiv = []
            diverseGroupsAttrs.forEach((attr) =>{
                if (workingValues[attr]){
                    overallDiv.push(prettyText[attr])
                }
            })
            finalData['overallDiv'] = arrayListToString(overallDiv)

            // get most diverse fish season
            const fishSeason = {}
            fishDivSeasonAttrs.forEach((attr) =>{
                fishSeason[attr] = Math.max(...workingValues[attr])
            })
            
            finalData['fishDivSeason'] = prettyText[getPropForLargestValue(fishSeason)]
         
            // get most diverse mammalMonth 
            const mammalmonth = {}
            mmDivMonthAttrs.forEach((attr) =>{
                mammalmonth[attr] = Math.max(...workingValues[attr])
            })
            const mammalDivRes = getPropsForLargestValue(mammalmonth)
            finalData['mammalDivMonth'] = getPrettyTextList(mammalDivRes.keys)
            finalData['mammalDivCount'] = mammalDivRes.value


            // get most diverse bird Month 
            const birdmonth = {}
            birdDivMonthAttrs.forEach((attr) =>{
                birdmonth[attr] = Math.max(...workingValues[attr])
            })
            const birdDivRes = getPropsForLargestValue(birdmonth)
            finalData['birdDivMonth'] = getPrettyTextList(birdDivRes.keys)
            finalData['birdDivCount'] = birdDivRes.value

            // get most diverse coastal bird Month 
            console.log(workingValues)
            const coastalbirdmonth = {}
            coastalBirdDivMonthAttrs.forEach((attr) =>{
                coastalbirdmonth[attr] = Math.max(...workingValues[attr])
            })
            const coastalbirdDivRes = getPropsForLargestValue(coastalbirdmonth)
            console.log(coastalbirdDivRes)
            finalData['coastalBirdDivMonth'] = getPrettyTextList(coastalbirdDivRes.keys)
            finalData['coastalBirdDivCount'] = coastalbirdDivRes.value

            // estimated / confirmed metrics.  Right nowe, just Corals
            estConfMetrics.forEach((attr) =>{
                finalData[attr] =  Math.max(...workingValues[attr])
            })

            // populate cell values into endangered/threated/SGCN species object
            const summaryItems = {}
            const summaryItemsFinal  ={}
            for (const [theme, spArr] of Object.entries(potentialSPLists)) {
                summaryItems[theme] = []
                console.log(workingValues[theme])
                for (const [sp, values] of Object.entries(workingValues[theme])) {
                    if (Math.max(...values) == 1 && !summaryItems[theme].includes(AllCodes[sp])){
                        summaryItems[theme].push(AllCodes[sp])
                    }
                }
            }
            for (const [theme, textArr] of Object.entries(summaryItems)) {
               summaryItemsFinal[theme] = arrayListToString(textArr)
            }
            console.log(summaryItemsFinal)
            finalData['summaryItems'] = summaryItemsFinal


            // console.log(finalData)
            setProcessedData(version.value, "SPECIES_SUMMARY", 'a', 'a', finalData)
            return finalData
        }
        else{
            return null
        }
    }
    else{
        // console.log("Loading ", processedData.value[version.value]['SPECIES_SUMMARY']['a']['a'])
        return processedData.value[version.value]['SPECIES_SUMMARY']['a']['a']
    }
    
})

const getPrettyTextList = (codeList) =>{
    if (codeList){
        let text = ""
        codeList.forEach((code) =>{
            console.log(code)
            text += prettyText[code] + ", "
        })
        return text.slice(0, -2); 
    }
    else{
        return null
    }
}

const prettyText = {
    "DIV_FIS" : "demersal fish", 
    "DIV_MM" : "marine mammals", 
    "DIV_BIO" : "offshore birds",
    "DIV_BIC" : "coastal birds",
    "DIV_FISS" : "spring",  
    "DIV_FISF" : "fall",
    "DIV_MM01" : "January", 
    "DIV_MM02": "February", 
    "DIV_MM03": "March", 
    "DIV_MM04": "April", 
    "DIV_MM05": "May", 
    "DIV_MM06": "June", 
    "DIV_MM07": "July", 
    "DIV_MM08": "August",
    "DIV_MM09": "September",
    "DIV_MM10": "October",
    "DIV_MM11": "November", 
    "DIV_MM12": "December",
    "DIV_BIO01" : "January", 
    "DIV_BIO02": "February", 
    "DIV_BIO03": "March", 
    "DIV_BIO04": "April", 
    "DIV_BIO05": "May", 
    "DIV_BIO06": "June", 
    "DIV_BIO07": "July", 
    "DIV_BIO08": "August",
    "DIV_BIO09": "September",
    "DIV_BIO10": "October",
    "DIV_BIO11": "November", 
    "DIV_BIO12": "December",
    "DIV_BIC_01": "January", 
    "DIV_BIC_02": "February", 
    "DIV_BIC_03": "March", 
    "DIV_BIC_04": "April", 
    "DIV_BIC_05": "May", 
    "DIV_BIC_06": "June", 
    "DIV_BIC_07": "July", 
    "DIV_BIC_08": "August",
    "DIV_BIC_09": "September",
    "DIV_BIC_10": "October",
    "DIV_BIC_11": "November", 
    "DIV_BIC_12": "December",
    "KCOR" : "Coral habitat",
    null: "None / No Data"
}

const potentialSPLists = {
    "esaMammals" :["BLWH", "FIWH", "KIWH", "RIWH", "SEWH", "SPWH"],
    "esaBirds" : ["BCPE", "ROST"],
    "esaTurtles" :["GRTU", "LETU", "KRTU", "LOTU"],
    "stateBirds" :["ARTE", "ATPU", "BLTE", "COLO", "COTE", "FOTE", "LETE", "RAZO"],
    "sgcnFish" :["SP022", "SP027", "SP032", "SP033", "SP034", "SP035", "SP073", "SP106", "SP139", "SP145", "SP380", "SP384"],
    "saltmarsh": ["BLTE", "FOTE", "BLSC"],
    "sand" : ["ARTE", "COTE", "LETE", "ROST"],
    "rocky": ["ARTE", "ATPU", "RAZO", "ROST", "COEI"],
    "esaCoastalBirds" : ["CB_PIPL", "CB_ROST", "CB_REKN"],
    "stateCoastalBirds" :["CB_AMOY",  "CB_BLTE", "CB_LEBI", "CB_LETE", "CB_BLSK", "CB_COTE", "CB_FOTE", "CB_GBTE", "CB_ROYT"],
    "sgcnCoastalBirds" :["CB_REKN", "CB_PUSA", "CB_PIPL", "CB_ROST", "CB_BLSK", "CB_LETE", "CB_AMBD", "CB_RTLO", "CB_WWSC"],
}

const goTo = (obj) =>{
    if(obj.params){
        if (obj.params.type == 'habSpotlight'){
            setActiveHabSpotlight(obj.params.value)
            setProcessedData('habSpotlight', obj.params.value)
        }
    }
    if (obj.link){
        // console.log(route.fullPath)
        const path = `${obj.link}`
        router.push(path)
    }
    // console.log(obj)
}

onMounted(() => {
    // if coming from a route (entered via URL).  Load the data
    if (activeLayerID.value != 'SPECIES_SUMMARY'){
        loadDataByID('SPECIES_SUMMARY', version)
    }
})

</script>
<style scope>

</style>